import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ArrowClockwise, CheckCircle, Circle, X } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useBusiness } from '../../context/BusinessContext';
import { usePermissions } from '../../hooks/usePermissions';

const openStorageKey = (userId, businessId) =>
  `idotoo.supplierOnboarding.open.${userId || 'anon'}.${businessId || 'none'}`;

const TRIGGER_BG = '#D1B1EC';
const TRIGGER_BG_HOVER = '#c4a0e3';
const TRIGGER_FG = '#1a1a1a';
const ONBOARDING_PROGRESS_EVENT = 'supplier-onboarding-progress-changed';

/** Clear sticky VerificationBanner (pink strip) + mobile bottom nav where present */
const ANCHOR_BOTTOM = {
  xs: 'calc(72px + 56px + env(safe-area-inset-bottom, 0px))',
  md: 'calc(80px + env(safe-area-inset-bottom, 0px))',
};

function filterVisibleSubtasks(subtasks, canAny) {
  return subtasks.filter(
    (st) => !st.permissions || st.permissions.length === 0 || canAny(st.permissions)
  );
}

/** First stage that still has an incomplete visible subtask; otherwise last stage with visible work. */
function getCurrentStageIndex(stages, canAny) {
  let lastVisibleStage = -1;
  for (let i = 0; i < stages.length; i++) {
    const visible = filterVisibleSubtasks(stages[i].subtasks, canAny);
    if (visible.length === 0) continue;
    lastVisibleStage = i;
    const allDone = visible.every((st) => st.completed);
    if (!allDone) return i;
  }
  return lastVisibleStage >= 0 ? lastVisibleStage : 0;
}

function GetToKnowIdotooPanel() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedBusiness, loading: businessLoading, hasNoBusiness } = useBusiness();
  const { canAny } = usePermissions();

  const businessId = selectedBusiness?.business?._id;
  const userId = user?.id || user?._id;
  const firstName = (user?.firstName || 'there').trim() || 'there';

  const [panelOpen, setPanelOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [widgetHidden, setWidgetHidden] = useState(false);
  const [stages, setStages] = useState([]);
  const [dismissLoading, setDismissLoading] = useState(false);
  const [error, setError] = useState('');
  const refreshTimerRef = useRef(null);

  useEffect(() => {
    if (!userId || !businessId) return;
    try {
      const raw = localStorage.getItem(openStorageKey(userId, businessId));
      if (raw === '1') setPanelOpen(true);
    } catch {
      /* ignore */
    }
  }, [userId, businessId]);

  const persistPanelOpen = useCallback(
    (next) => {
      setPanelOpen(next);
      if (!userId || !businessId) return;
      try {
        localStorage.setItem(openStorageKey(userId, businessId), next ? '1' : '0');
      } catch {
        /* ignore */
      }
    },
    [userId, businessId]
  );

  const fetchProgress = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(`/api/business/${businessId}/onboarding`);
      setWidgetHidden(!!data.widgetHidden);
      setStages(Array.isArray(data.stages) ? data.stages : []);
    } catch (e) {
      setError(e.response?.data?.message || 'Could not load guide');
      setStages([]);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  const scheduleProgressRefresh = useCallback(() => {
    if (!businessId || hasNoBusiness) return;
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    refreshTimerRef.current = window.setTimeout(() => {
      refreshTimerRef.current = null;
      fetchProgress();
    }, 500);
  }, [businessId, hasNoBusiness, fetchProgress]);

  useEffect(() => {
    if (!businessId || hasNoBusiness) return;
    fetchProgress();
  }, [businessId, hasNoBusiness, fetchProgress]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible' && businessId && !hasNoBusiness) {
        fetchProgress();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [businessId, hasNoBusiness, fetchProgress]);

  useEffect(() => {
    const onProgressChanged = () => {
      scheduleProgressRefresh();
    };
    window.addEventListener(ONBOARDING_PROGRESS_EVENT, onProgressChanged);
    return () => {
      window.removeEventListener(ONBOARDING_PROGRESS_EVENT, onProgressChanged);
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [scheduleProgressRefresh]);

  const { visibleSubtaskTotal, visibleSubtaskCompleted, currentStageIndex, allDone } = useMemo(() => {
    let total = 0;
    let completed = 0;
    for (const stage of stages) {
      const vis = filterVisibleSubtasks(stage.subtasks, canAny);
      for (const st of vis) {
        total += 1;
        if (st.completed) completed += 1;
      }
    }
    const cur = stages.length ? getCurrentStageIndex(stages, canAny) : 0;
    const done = total > 0 && completed === total;
    return {
      visibleSubtaskTotal: total,
      visibleSubtaskCompleted: completed,
      currentStageIndex: cur,
      allDone: done
    };
  }, [stages, canAny]);

  const progressPct =
    visibleSubtaskTotal === 0
      ? 0
      : Math.round((visibleSubtaskCompleted / visibleSubtaskTotal) * 100);

  /** Stages this user actually sees (≥1 subtask); header is Step i / this count */
  const { stepNumber, stepTotal } = useMemo(() => {
    const visibleIndices = [];
    stages.forEach((s, i) => {
      if (filterVisibleSubtasks(s.subtasks, canAny).length > 0) visibleIndices.push(i);
    });
    const total = visibleIndices.length;
    const pos = visibleIndices.indexOf(currentStageIndex);
    const step = pos >= 0 ? pos + 1 : Math.max(1, total);
    return { stepNumber: total === 0 ? 1 : step, stepTotal: Math.max(total, 1) };
  }, [stages, currentStageIndex, canAny]);

  const hasLaterStagesWithVisibleTasks = useMemo(() => {
    return stages.slice(currentStageIndex + 1).some((s) => {
      const vis = filterVisibleSubtasks(s.subtasks, canAny);
      return vis.length > 0;
    });
  }, [stages, currentStageIndex, canAny]);

  const handleDismissGuide = async () => {
    if (!businessId) return;
    setDismissLoading(true);
    setError('');
    try {
      await api.patch(`/api/business/${businessId}/onboarding-widget`, { skipped: true });
      setWidgetHidden(true);
      persistPanelOpen(false);
    } catch (e) {
      setError(e.response?.data?.message || 'Could not dismiss');
    } finally {
      setDismissLoading(false);
    }
  };

  if (businessLoading || hasNoBusiness || !businessId || !userId) {
    return null;
  }

  if (widgetHidden) {
    return null;
  }

  const fixedAnchorSx = {
    position: 'fixed',
    right: { xs: 20, sm: 28 },
    bottom: ANCHOR_BOTTOM,
    zIndex: (theme) => theme.zIndex.drawer + 2,
    display: 'flex',
    flexDirection: 'column-reverse',
    alignItems: 'flex-end',
    gap: panelOpen ? 1.5 : 0,
  };

  const triggerSx = {
    borderRadius: 2,
    px: 2.5,
    py: 1,
    fontWeight: 700,
    textTransform: 'none',
    boxShadow: 3,
    backgroundColor: TRIGGER_BG,
    color: TRIGGER_FG,
    '&.MuiButton-contained': {
      backgroundColor: TRIGGER_BG,
      color: TRIGGER_FG,
      '&:hover': { backgroundColor: TRIGGER_BG_HOVER },
    },
  };

  const currentStage = stages[currentStageIndex];
  const currentVisible = currentStage
    ? filterVisibleSubtasks(currentStage.subtasks, canAny)
    : [];
  const isCompletionPage = !loading && !error && allDone && visibleSubtaskTotal > 0;

  return (
    <Box sx={fixedAnchorSx}>
      <Button
        variant="contained"
        disableElevation
        onClick={() => persistPanelOpen(!panelOpen)}
        sx={triggerSx}
      >
        👋 Get to know idotoo
      </Button>

      {panelOpen && (
        <Paper
          elevation={8}
          role="dialog"
          aria-label={isCompletionPage ? 'Onboarding complete' : `Onboarding step ${stepNumber} of ${stepTotal}`}
          sx={{
            width: { xs: 'min(100vw - 24px, 380px)', sm: 380 },
            maxHeight: { xs: 'min(70vh, 520px)', sm: 520 },
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: (theme) =>
              theme.palette.mode === 'dark'
                ? '0 12px 40px rgba(0,0,0,0.5)'
                : '0 12px 40px rgba(0,0,0,0.14)',
          }}
        >
          {!isCompletionPage && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1.25,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
                  Step {stepNumber}/{stepTotal}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    aria-label="Refresh progress"
                    onClick={fetchProgress}
                    disabled={loading}
                    sx={{ color: 'text.secondary' }}
                  >
                    <ArrowClockwise size={21} />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="Close guide"
                    onClick={() => persistPanelOpen(false)}
                    sx={{ color: 'text.secondary' }}
                  >
                    <X size={22} />
                  </IconButton>
                </Box>
              </Box>

              <Divider />
            </>
          )}

          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              px: isCompletionPage ? 3 : 2,
              pt: isCompletionPage ? 4 : 2,
              pb: isCompletionPage ? 4 : 1,
              minHeight: 0,
            }}
          >
            {isCompletionPage ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 42,
                    bgcolor: (theme) =>
                      alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.16 : 0.1),
                  }}
                  aria-hidden="true"
                >
                  🎉
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  You&apos;re all set!
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleDismissGuide}
                  disabled={dismissLoading}
                  sx={{ textTransform: 'none', fontWeight: 700, mt: 1 }}
                >
                  I&apos;m all done
                </Button>
              </Box>
            ) : (
              <>
                <Typography variant="body1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Hi {firstName}, let&apos;s get you comfortable.
                </Typography>

                <Box sx={{ mt: 2, mb: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={progressPct}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'action.hover',
                      '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: 'primary.main' },
                    }}
                  />
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                    {progressPct}% completed
                    {progressPct > 0 && (
                      <Box component="span" sx={{ color: 'warning.main', fontWeight: 600, ml: 0.5 }}>
                        — Nice work!
                      </Box>
                    )}
                  </Typography>
                </Box>

                {loading ? (
                  <Typography variant="body2" color="text.secondary">
                    Loading…
                  </Typography>
                ) : error ? (
                  <Typography variant="body2" color="error">
                    {error}
                  </Typography>
                ) : currentStage && currentVisible.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                      {currentStage.title}
                    </Typography>
                    <List dense disablePadding>
                      {currentVisible.map((st) => (
                        <ListItem
                          key={st.id}
                          disableGutters
                          secondaryAction={!st.completed && (
                            <Button
                              size="small"
                              onClick={() => navigate(st.ctaPath)}
                              sx={{ fontWeight: 600, textTransform: 'none', minWidth: 0 }}
                            >
                              Go
                            </Button>
                          )}
                          sx={{
                            alignItems: 'flex-start',
                            pr: st.completed ? 0 : 7,
                            py: 0.75,
                            '&:hover': { bgcolor: 'transparent' },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32, mt: 0.25 }}>
                            {st.completed ? (
                              <Box component="span" sx={{ color: 'primary.main', display: 'inline-flex' }}>
                                <CheckCircle size={22} weight="fill" />
                              </Box>
                            ) : (
                              <Box component="span" sx={{ color: 'text.disabled', display: 'inline-flex' }}>
                                <Circle size={22} />
                              </Box>
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={st.label}
                            primaryTypographyProps={{
                              variant: 'body2',
                              fontWeight: st.completed ? 500 : 600,
                              sx: st.completed
                                ? { color: 'text.disabled', textDecoration: 'line-through' }
                                : { color: 'text.primary' }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                    {hasLaterStagesWithVisibleTasks && !allDone && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25, lineHeight: 1.4 }}>
                        Complete this stage to unlock the next.
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>

          {!isCompletionPage && (
            <>
              <Divider />

              <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleDismissGuide}
                  disabled={dismissLoading}
                  sx={{ textTransform: 'none', color: 'text.secondary' }}
                >
                  Don&apos;t show this guide
                </Button>
              </Box>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
}

export default GetToKnowIdotooPanel;
